import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { BREAKPOINT, useAuth, useMediaQuery } from '@wa/shared';
import { SECTIONS, maySeeSection, type SettingsSection } from '@/settings/sections';

/**
 * The settings area: a column of sections on the left, the chosen one on the right.
 *
 * This replaces a screen called Team with five pill tabs across the top, which
 * three different rail entries pointed into at three different tabs. The reference
 * has one Settings entry and, inside it, a column that lists every section with a
 * name and a line saying what it is for — so the column is the map, and the
 * question "where do I change working hours" is answered by reading, not by trying
 * each tab.
 *
 * <b>Every section is a URL.</b> `/settings/routing` is a link somebody can send;
 * `/team?tab=routing` was a link into a piece of component state that happened to
 * survive a reload. The old addresses redirect, so nothing anybody bookmarked
 * breaks.
 *
 * <b>On a phone the column is the first screen and the section is the second.</b>
 * Below `md` there is no room for both, and stacking a 5-row list above a form
 * puts the form 500px down. So `/settings` shows the list, tapping a row navigates,
 * and the section's header grows a back chevron. Above `md` the index redirects to
 * the first section the person may open, because an empty right-hand pane beside
 * a list is a screen that asks a question instead of answering one.
 */
export function SettingsLayout() {
  const { user } = useAuth();
  const wide = useMediaQuery(`(min-width: ${BREAKPOINT.md}px)`);
  const { section: current } = useParams();

  const sections = useMemo(() => SECTIONS.filter((s) => maySeeSection(user, s)), [user]);

  if (sections.length === 0) {
    return (
      <div className="grid h-full place-items-center p-6 text-center text-small text-(--color-muted)">
        You do not have permission to change this account&rsquo;s settings.
      </div>
    );
  }

  const atIndex = current === undefined;

  return (
    <div className="flex h-full min-h-0 flex-col bg-(--color-paper) md:flex-row">
      {/* On a phone the list is a screen of its own, and a screen has a heading.
          The inbox went months without one; screen readers announce it and
          sighted people orient by it, and its absence is invisible in a mockup. */}
      {!wide && atIndex && (
        <header className="flex h-14 shrink-0 items-center border-b border-(--color-line) bg-(--color-surface) px-4">
          <h1 className="text-lead font-semibold tracking-tight">Settings</h1>
        </header>
      )}
      {(wide || atIndex) && <SectionColumn sections={sections} current={current} wide={wide} />}
      {(wide || !atIndex) && (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <Outlet context={{ sections, wide } satisfies OutletShape} />
        </div>
      )}
    </div>
  );
}

interface OutletShape { sections: SettingsSection[]; wide: boolean }

/** `/settings` on its own: a list on a phone, the first section on a desktop. */
export function SettingsIndex() {
  const wide = useMediaQuery(`(min-width: ${BREAKPOINT.md}px)`);
  const { user } = useAuth();
  const first = SECTIONS.find((s) => maySeeSection(user, s));
  if (wide && first) return <Navigate to={`/settings/${first.key}`} replace />;
  return null;
}

/**
 * The old Team screen's addresses. `?tab=` was the only state the tabs had, and it
 * maps one to one onto the new sections.
 */
export function TeamRedirect() {
  const { search } = useLocation();
  const tab = new URLSearchParams(search).get('tab');
  const known = SECTIONS.some((s) => s.key === tab);
  return <Navigate to={known ? `/settings/${tab}` : '/settings'} replace />;
}

// --------------------------------------------------------------- the column --

function SectionColumn({ sections, current, wide }: {
  sections: SettingsSection[]; current: string | undefined; wide: boolean;
}) {
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const shown = needle
    ? sections.filter((s) => `${s.label} ${s.blurb}`.toLowerCase().includes(needle))
    : sections;

  return (
    <nav
      aria-label="Settings sections"
      className={[
        'flex min-h-0 shrink-0 flex-col border-(--color-line) bg-(--color-surface)',
        wide ? 'w-72 border-r xl:w-80' : 'w-full',
      ].join(' ')}
    >
      {/* The reference puts a search box at the top of the column. With five
          sections it is close to decorative; it is kept because the list will not
          stay at five, and because it does something — a box that filters nothing
          would be the one control on the screen that lies. */}
      <div className="flex h-14 shrink-0 items-center gap-2 border-b border-(--color-line) px-4">
        <svg viewBox="0 0 20 20" aria-hidden="true" className="size-4.5 shrink-0 text-(--color-faint)">
          <circle cx="8.8" cy="8.8" r="5.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <path d="m12.8 12.8 3.6 3.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search settings…"
          aria-label="Search settings"
          className="min-w-0 flex-1 bg-transparent text-body text-(--color-ink) outline-none placeholder:text-(--color-faint)"
        />
      </div>

      <ul className="pane-scroll min-h-0 flex-1 overflow-y-auto">
        {shown.map((s) => (
          <li key={s.key}>
            <NavLink
              to={`/settings/${s.key}`}
              aria-current={current === s.key ? 'page' : undefined}
              className={[
                'flex gap-3 border-b border-(--color-line-dim) px-4 py-3.5 transition',
                'border-l-[3px]',
                current === s.key
                  ? 'border-l-(--color-brand) bg-(--color-brand-wash)'
                  : 'border-l-transparent hover:bg-(--color-sunken)',
              ].join(' ')}
            >
              <span className={[
                'mt-0.5 shrink-0',
                current === s.key ? 'text-(--color-brand)' : 'text-(--color-ink-soft)',
              ].join(' ')}>
                {s.icon}
              </span>
              <span className="min-w-0">
                <span className="block text-lead font-semibold leading-snug text-(--color-ink)">{s.label}</span>
                <span className="mt-0.5 block text-small leading-snug text-(--color-muted)">{s.blurb}</span>
              </span>
            </NavLink>
          </li>
        ))}
        {shown.length === 0 && (
          <li className="px-4 py-6 text-center text-small text-(--color-muted)">
            Nothing matches &ldquo;{q.trim()}&rdquo;.
          </li>
        )}
      </ul>
    </nav>
  );
}

// ----------------------------------------------------------- the right pane --

interface HeaderAction { node: React.ReactNode }
const ActionContext = createContext<((a: React.ReactNode) => void) | null>(null);

/**
 * A section's screen: the header row the reference draws — title on the left, the
 * screen's one action on the right — and a scrolling body under it.
 *
 * The action is a slot a section fills from inside itself with `useHeaderAction`,
 * because the thing on the right of "Push Notifications" is "Test notification":
 * it belongs to the section, not to the frame, and only the section knows whether
 * it is enabled.
 */
export function SettingsScreen({ section, children }: { section: string; children: React.ReactNode }) {
  const wide = useMediaQuery(`(min-width: ${BREAKPOINT.md}px)`);
  const navigate = useNavigate();
  const meta = SECTIONS.find((s) => s.key === section);
  const [action, setAction] = useState<HeaderAction>({ node: null });

  /*
   * Stable, and this is not a micro-optimisation — it is the difference between the
   * settings area working and React giving up.
   *
   * This was `value={(node) => setAction({ node })}` written inline in the JSX below,
   * which is a NEW function identity on every render. `useHeaderAction`'s effect
   * depends on it, so: render → effect → setAction → render → a different function →
   * effect again → forever. "Maximum update depth exceeded", a hundred times a
   * second, and the screen is unusable.
   *
   * What hid it is that this slot was written for the Notifications screen months
   * before that screen existed, and nothing else in Settings has a header action —
   * so the first caller was also the first person to find out. The hook's own comment
   * promises that `deps` controls when the node is re-set; an unstable `set` in the
   * dependency array quietly made that promise impossible to keep.
   */
  const put = useCallback((node: React.ReactNode) => setAction({ node }), []);

  // A section a person may not open still needs an answer, and the answer is
  // the column, not a blank pane.
  const { user } = useAuth();
  if (!meta || !maySeeSection(user, meta)) return <Navigate to="/settings" replace />;

  return (
    <ActionContext.Provider value={put}>
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-(--color-line) bg-(--color-surface) px-3 sm:px-5">
        {!wide && (
          <button
            type="button"
            onClick={() => navigate('/settings')}
            aria-label="Back to settings"
            className="tap -ml-1 grid place-items-center rounded text-(--color-muted) hover:text-(--color-ink)"
          >
            <svg viewBox="0 0 20 20" aria-hidden="true" className="size-5">
              <path d="M12.5 4.5 7 10l5.5 5.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
        <h1 className="min-w-0 flex-1 truncate text-lead font-semibold tracking-tight">{meta.label}</h1>
        {action.node && <div className="shrink-0">{action.node}</div>}
      </header>

      <div className="pane-scroll min-h-0 flex-1 overflow-y-auto">
        <div className="@container mx-auto flex max-w-6xl flex-col gap-4 p-3 sm:p-5">
          {children}
        </div>
      </div>
    </ActionContext.Provider>
  );
}

/**
 * Puts something on the right of the section's header.
 *
 * `deps` says when the node has meaningfully changed — a disabled flag, a count —
 * the same contract as `useEffect`. Without it a fresh element every render would
 * set state every render, and the frame and the section would take turns
 * re-rendering each other until React gave up.
 *
 * <b>That is only true while the setter from the context is stable</b>, which is
 * what `put` in `SettingsScreen` is for. It was not, once, and `deps` then controlled
 * nothing: the identity of the setter changed on every render and dragged the effect
 * round with it. The node itself is deliberately NOT in the dependency array — it is
 * a fresh element every render by nature, which is the whole reason `deps` exists.
 */
export function useHeaderAction(node: React.ReactNode, deps: React.DependencyList) {
  const set = useContext(ActionContext);

  // Read through a ref so the current node is used without being a dependency.
  const latest = useRef(node);
  latest.current = node;

  useEffect(() => {
    set?.(latest.current);
    return () => set?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [set, ...deps]);
}
